import React, { useState } from "react";
import api from "@/lib/api";
import { useAdmin } from "@/context/AdminContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Lock, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

// Full-screen modal — cannot be dismissed until the admin sets a fresh password
export default function ForcePasswordReset() {
  const { admin, refresh } = useAdmin();
  const [cur, setCur] = useState("");
  const [np, setNp] = useState("");
  const [np2, setNp2] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (np !== np2) return toast.error("New passwords don't match");
    if (np.length < 8) return toast.error("Password must be at least 8 characters");
    setBusy(true);
    try {
      await api.post("/admin/change-password", { current_password: cur, new_password: np });
      toast.success("Password updated — you're all set");
      if (refresh) await refresh();
      else window.location.reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update password");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-md flex items-center justify-center px-4" data-testid="force-pw-reset-modal">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2.5 mb-6">
          <img src="/zenith360-logo.png" alt="Zenith 360" className="h-10 w-auto" />
          <div>
            <div className="font-heading font-bold text-lg text-logo-gradient">Set a new password</div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mt-0.5">Required on first sign-in</div>
          </div>
        </div>
        <form onSubmit={submit} className="rounded-sm bg-card backdrop-blur-xl p-6 space-y-4 border border-border">
          <div className="text-sm text-foreground/80 leading-relaxed">
            Welcome, <span className="text-foreground font-medium">{admin?.full_name || admin?.email}</span>. Please rotate the temporary password before you get to the admin console.
          </div>
          <div>
            <Label className="text-foreground/90">Current password</Label>
            <Input type="password" required value={cur} onChange={(e) => setCur(e.target.value)}
              className="mt-1 bg-background text-foreground border-border focus:border-orange-500"
              data-testid="pw-current" autoFocus />
          </div>
          <div>
            <Label className="text-foreground/90">New password</Label>
            <Input type="password" required minLength={8} value={np} onChange={(e) => setNp(e.target.value)}
              className="mt-1 bg-background text-foreground border-border focus:border-orange-500"
              data-testid="pw-new" />
            <div className="text-[10px] text-muted-foreground/80 mt-1">At least 8 characters. Use a mix of letters, numbers and symbols.</div>
          </div>
          <div>
            <Label className="text-foreground/90">Confirm new password</Label>
            <Input type="password" required minLength={8} value={np2} onChange={(e) => setNp2(e.target.value)}
              className="mt-1 bg-background text-foreground border-border focus:border-orange-500"
              data-testid="pw-confirm" />
          </div>
          <Button type="submit" disabled={busy}
            className="w-full rounded-sm bg-gradient-to-r from-orange-600 to-red-600 hover:opacity-90 text-foreground h-11"
            data-testid="pw-submit">
            <Lock className="w-4 h-4 mr-2" /> {busy ? "Updating…" : "Update password & continue"}
          </Button>
          <Button type="button" variant="outline" disabled={busy}
            onClick={async () => {
              if (!window.confirm("Keep your current password? You can rotate it later from the topbar → Change password.")) return;
              setBusy(true);
              try {
                await api.post("/auth/dismiss-reset-prompt", {});
                toast.success("Reset prompt dismissed — welcome!");
                if (refresh) await refresh();
                else window.location.reload();
              } catch (err) {
                toast.error(err?.response?.data?.detail || "Could not dismiss reset prompt");
              } finally { setBusy(false); }
            }}
            className="w-full rounded-sm bg-card hover:bg-muted text-foreground/90 border-border h-9 text-xs"
            data-testid="pw-dismiss">
            I'll keep this password — take me in
          </Button>
          <div className="text-[10px] text-muted-foreground/80 font-mono text-center pt-1 flex items-center justify-center gap-1.5">
            <ShieldCheck className="w-3 h-3" /> Passwords are hashed with bcrypt; nothing plain-text is ever stored.
          </div>
        </form>
      </div>
    </div>
  );
}
